import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../state/fetchOptions.state';
import { SchoolClassJsonModel } from '../school-class.json.model';
import { SchoolClassActions } from './school-class.actions';
import { SchoolClassService } from '../school-class.service';
import { tap } from 'rxjs';

export interface SchoolClassStateModel {
  schoolClasses: SchoolClassJsonModel[]
}

@State<SchoolClassStateModel>({
  name: 'schoolClass',
  defaults: {
    schoolClasses: []
  }
})
@Injectable()
export class SchoolClassState {
  private readonly sequenceService = inject(SchoolClassService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: SchoolClassStateModel) {
    return state;
  }

  @Selector()
  static getSchoolClasses(state: SchoolClassStateModel) {
    return state.schoolClasses;
  }

  @Selector()
  static getAmountOfSchoolClasses(state: SchoolClassStateModel) {
    return state.schoolClasses?.length ?? 0;
  }

  @Selector()
  static schoolClassesIsEmpty(state: SchoolClassStateModel) {
    return (state.schoolClasses?.length ?? 0) <= 0;
  }

  @Action(SchoolClassActions.Fetch)
  onFetchSchoolClasses(ctx: StateContext<SchoolClassStateModel>, action: SchoolClassActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'grade'
    };

    return this.sequenceService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new SchoolClassActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(SchoolClassActions.FetchSuccess)
  onFetchSchoolClassesSuccess(ctx: StateContext<SchoolClassStateModel>, { payload }: SchoolClassActions.FetchSuccess) {
    ctx.setState({ schoolClasses: payload });
  }

  @Action(SchoolClassActions.FetchAppend)
  onFetchAppendSchoolClasses(ctx: StateContext<SchoolClassStateModel>, action: SchoolClassActions.FetchAppend) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'grade'
    };

    return this.sequenceService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new SchoolClassActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(SchoolClassActions.FetchSuccessAppend)
  onFetchSchoolClassesSuccessAppend(ctx: StateContext<SchoolClassStateModel>, { payload }: SchoolClassActions.FetchSuccess) {
    const state = ctx.getState();
    ctx.setState({
      schoolClasses: [...state.schoolClasses, ...payload]
    });
  }

  @Action(SchoolClassActions.Delete)
  onDelete(ctx: StateContext<SchoolClassJsonModel>, { payload }: SchoolClassActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.type,
        itemType: 'Schulklassen',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new SchoolClassActions.Fetch());
        })
      );
  }
}
